import {
  computeCapacityBottleneck,
  computeFunnelVolumeMetrics,
  computeKpis,
  computePnL,
  sensitivityAnalysis,
  totalVisitorsFromChannels,
} from "@/lib/funnel-planner/engine";
import { getChannelDef } from "@/lib/funnel-planner/channels";
import type { FunnelTemplateId, FunnelWizardState, TrafficChannel } from "@/lib/funnel-planner/schema";

export type RecommendationSeverity = "info" | "warning" | "critical";
export type RecommendationSource = "benchmark_default" | "user_input" | "calculated" | "estimated";

export type FunnelRecommendation = {
  id: string;
  severity: RecommendationSeverity;
  source: RecommendationSource;
  title: string;
  detail: string;
  action: string;
};

const TEMPLATE_CHANNEL_RECOMMENDATIONS: Record<FunnelTemplateId, string[]> = {
  "agency-white-label": ["google_organic", "linkedin", "email_newsletter", "retargeting"],
  "franchise-multi-location": ["google_organic", "google_ads", "review_sites", "retargeting"],
  "ai-search-geo": ["chatgpt_search", "google_organic", "perplexity_ai", "linkedin"],
  "adult-compliance": ["google_organic", "display_ads", "reddit_organic", "retargeting"],
  "b2b-enterprise": ["google_organic", "linkedin", "linkedin_ads", "email_newsletter"],
  "ecommerce-dtc": ["google_ads", "facebook_ads", "email_newsletter", "retargeting"],
  "high-ticket-coaching": ["google_organic", "facebook_organic", "youtube", "retargeting"],
  "saas-plg": ["google_organic", "google_ads", "review_sites", "retargeting"],
};

const REGIONAL_CHANNEL_RECOMMENDATIONS: Record<string, string[]> = {
  JP: ["yahoo_organic", "yahoo_ads"],
  KR: ["youtube", "google_ads"],
  CN: ["bing_organic", "other_search"],
  IN: ["youtube", "whatsapp"],
  BR: ["instagram", "facebook_ads"],
  IL: ["google_organic", "facebook_ads"],
  EU: ["google_organic", "linkedin"],
  UK: ["google_organic", "linkedin"],
};

const PAID_MODES = new Set(["cpc", "cpm", "cpv", "flat_fee", "cpa", "cost_per_lead"]);

function isPaidChannel(channelId: string): boolean {
  const def = getChannelDef(channelId);
  return def ? PAID_MODES.has(def.defaultAcquisitionType) : false;
}

function selectedChannelIds(channels: TrafficChannel[]): Set<string> {
  return new Set(channels.map((channel) => channel.id));
}

function channelRecommendationIds(state: FunnelWizardState): string[] {
  if (!state.templateId) return [];
  const budget = state.audienceGoalDetails.budgetCeiling ?? 0;
  const region = state.regionalSettings.region.toUpperCase();
  const base = TEMPLATE_CHANNEL_RECOMMENDATIONS[state.templateId] ?? [];
  const regional = REGIONAL_CHANNEL_RECOMMENDATIONS[region] ?? [];
  const ordered = [...regional, ...base].filter((id, index, ids) => ids.indexOf(id) === index);
  if (budget > 0 && budget < 1_500) {
    return ordered.filter((id) => !isPaidChannel(id));
  }
  if (budget >= 3_000 && !ordered.includes("retargeting")) {
    return [...ordered, "retargeting"];
  }
  return ordered;
}

export function getPlannerRecommendations(state: FunnelWizardState): FunnelRecommendation[] {
  const recommendations: FunnelRecommendation[] = [];
  const visitors = totalVisitorsFromChannels(state.trafficChannels, state.projectionInputs.monthlyVisitors);
  const metrics = computeFunnelVolumeMetrics(state.stages, state.projectionInputs, state.trafficChannels, state.graph);
  const pnl = computePnL(
    state.products,
    state.stages,
    state.trafficChannels,
    state.projectionInputs,
    0,
    state.regionalSettings,
    state.graph,
    state.expenses,
  );
  const kpis = computeKpis(pnl, state.products, state.stages, state.trafficChannels, state.projectionInputs, state.graph);
  const sensitivity = sensitivityAnalysis(state.stages, state.products, state.trafficChannels, state.projectionInputs, state.graph);
  const capacity = computeCapacityBottleneck(state);
  const currentChannelIds = selectedChannelIds(state.trafficChannels);
  const recommendedChannelId = channelRecommendationIds(state).find((id) => !currentChannelIds.has(id));
  const recommendedChannel = recommendedChannelId ? getChannelDef(recommendedChannelId) : undefined;

  if (state.trafficChannels.some((channel) => channel.trackingSetup === "none")) {
    recommendations.push({
      id: "tracking-missing",
      severity: "warning",
      source: "user_input",
      title: "Tracking is incomplete",
      detail: "At least one traffic channel has no tracking setup, so source-level ROI will be hard to validate.",
      action: "Add UTM or pixel/server-side tracking before scaling spend.",
    });
  }

  if (recommendedChannel && state.templateId) {
    const budgetContext =
      state.audienceGoalDetails.budgetCeiling != null && state.audienceGoalDetails.budgetCeiling < 1_500
        ? "low-budget"
        : state.audienceGoalDetails.budgetCeiling != null && state.audienceGoalDetails.budgetCeiling >= 3_000
          ? "scaling-budget"
          : "baseline";
    recommendations.push({
      id: `channel-recommendation-${recommendedChannel.id}`,
      severity: "info",
      source: "benchmark_default",
      title: `Add ${recommendedChannel.label} to round out the channel mix`,
      detail: `Recommended for the ${state.templateId} template with ${state.regionalSettings.region.toUpperCase()} regional defaults and ${budgetContext} planning.`,
      action: "Add this channel in step 2 or use it as the next traffic test before increasing spend.",
    });
  }

  if (visitors > 0 && metrics.customers === 0) {
    recommendations.push({
      id: "no-customers",
      severity: "critical",
      source: "calculated",
      title: "The funnel does not produce customers",
      detail: "Current traffic and conversion assumptions round down to zero monthly customers.",
      action: "Increase top-of-funnel volume, simplify stages, or improve the lowest-CVR stage.",
    });
  }

  const unrealisticStage = state.stages.find(
    (stage) => stage.conversionRate >= 75 && !["thank-you-page", "activation-event"].includes(stage.stageType),
  );
  if (unrealisticStage) {
    recommendations.push({
      id: `assumption-cvr-${unrealisticStage.id}`,
      severity: "warning",
      source: "user_input",
      title: "A stage CVR looks too aggressive",
      detail: `${unrealisticStage.name} is modeled at ${unrealisticStage.conversionRate.toFixed(1)}% conversion.`,
      action: "Stress-test this assumption with a lower CVR before using the forecast for budget decisions.",
    });
  }

  if (visitors >= 50_000 && state.trafficChannels.every((channel) => channel.confidence !== "high")) {
    recommendations.push({
      id: "assumption-traffic-confidence",
      severity: "warning",
      source: "user_input",
      title: "Traffic scale needs stronger confidence",
      detail: "The plan models high monthly traffic without any high-confidence channel source.",
      action: "Mark the channel confidence honestly or split the model into ramp phases.",
    });
  }

  const topLeverage = sensitivity[0];
  if (topLeverage && topLeverage.gap === "below") {
    recommendations.push({
      id: `leverage-${topLeverage.stageId}`,
      severity: "warning",
      source: "benchmark_default",
      title: `${topLeverage.stageName} is the strongest bottleneck`,
      detail: `It is below the planner reference rate and has the highest +10% conversion lift impact.`,
      action: "Prioritize copy, offer, page speed, proof, or sales-process tests on this stage first.",
    });
  }

  if (state.products.length > 0 && pnl.profitMarginPct < 20) {
    recommendations.push({
      id: "margin-low",
      severity: pnl.profitMarginPct < 0 ? "critical" : "warning",
      source: "calculated",
      title: "Margin is too thin",
      detail: `Modeled net margin is ${pnl.profitMarginPct.toFixed(1)}%, before any unmodeled overhead.`,
      action: "Review price, COGS, refund rate, traffic cost, or add a higher-margin upsell.",
    });
  }

  const primaryProduct = state.products.find((product) => !product.isUpsell && !["order_bump", "upsell", "downsell"].includes(product.offerRole ?? "core"));
  if (primaryProduct && kpis.cac != null) {
    const unitMargin = primaryProduct.price * (1 - primaryProduct.cogsPercent / 100 - primaryProduct.refundRatePercent / 100);
    if (unitMargin > 0 && kpis.cac > unitMargin) {
      recommendations.push({
        id: "assumption-cac-margin",
        severity: "critical",
        source: "calculated",
        title: "CAC is higher than unit margin",
        detail: `Modeled CAC is ${Math.round(kpis.cac).toLocaleString()}, above the primary offer margin.`,
        action: "Lower acquisition cost, increase price, improve CVR, or add a higher-margin backend offer.",
      });
    }
  }

  if (kpis.cacPaybackMonths != null && kpis.cacPaybackMonths > 6) {
    recommendations.push({
      id: "assumption-payback-long",
      severity: kpis.cacPaybackMonths > 12 ? "critical" : "warning",
      source: "calculated",
      title: "CAC payback is too long",
      detail: `Modeled CAC payback is ${kpis.cacPaybackMonths.toFixed(1)} months.`,
      action: "Tighten paid spend, improve activation, raise expansion revenue, or use a lower-risk traffic ramp.",
    });
  }

  if (kpis.roas != null && kpis.roas < 2) {
    recommendations.push({
      id: "roas-low",
      severity: "warning",
      source: "calculated",
      title: "ROAS is below a healthy scaling range",
      detail: `Current modeled ROAS is ${kpis.roas.toFixed(2)}x.`,
      action: "Hold paid scaling until conversion rate, AOV, or traffic cost improves.",
    });
  }

  if (capacity.bottleneck === "sales_capacity") {
    recommendations.push({
      id: "capacity-bottleneck",
      severity: "warning",
      source: "calculated",
      title: "Sales capacity caps the upside",
      detail: capacity.message,
      action: "Increase call capacity, tighten qualification, or push low-intent leads into nurture.",
    });
  }

  const hasOrderBump = state.products.some((product) => product.offerRole === "order_bump" || product.orderBumpEnabled);
  const hasUpsell = state.products.some((product) => product.offerRole === "upsell" || product.isUpsell);
  if (state.products.length > 0 && !hasOrderBump && ["ecommerce-dtc", "adult-compliance"].includes(state.templateId ?? "")) {
    recommendations.push({
      id: "offer-order-bump",
      severity: "info",
      source: "benchmark_default",
      title: "Add an order bump test",
      detail: "Commerce-style funnels usually need a low-friction basket lift before scaling paid traffic.",
      action: "Add a small order bump in step 3 and attach it to the checkout or order-form stage.",
    });
  }

  if (state.products.length > 0 && !hasUpsell && ["ecommerce-dtc", "high-ticket-coaching", "agency-white-label"].includes(state.templateId ?? "")) {
    recommendations.push({
      id: "offer-upsell",
      severity: "info",
      source: "benchmark_default",
      title: "Add a backend or upsell path",
      detail: "The current offer stack has no modeled upsell or backend revenue lever.",
      action: "Add an upsell, downsell, or backend offer in step 3 before finalizing the projection.",
    });
  }

  if (state.experiments.length === 0 && state.stages.length > 1) {
    const stage = topLeverage?.stageName ?? state.stages[0]?.name;
    recommendations.push({
      id: "experiment-first-test",
      severity: "info",
      source: "calculated",
      title: "Create the first controlled experiment",
      detail: stage ? `${stage} is the best first place to define a test.` : "No experiment is attached to the funnel yet.",
      action: "Add an A/B test in the experiment plan and choose one KPI to judge the winner.",
    });
  }

  if (recommendations.length === 0) {
    recommendations.push({
      id: "baseline-healthy",
      severity: "info",
      source: "estimated",
      title: "No critical modeling gap detected",
      detail: "The current assumptions are internally consistent for planning.",
      action: "Use the delivery roadmap and experiment plan to turn the model into an execution sequence.",
    });
  }

  return recommendations.slice(0, 6);
}

export function createPlannerExecutiveSummary(state: FunnelWizardState): string {
  const metrics = computeFunnelVolumeMetrics(state.stages, state.projectionInputs, state.trafficChannels, state.graph);
  const pnl = computePnL(
    state.products,
    state.stages,
    state.trafficChannels,
    state.projectionInputs,
    0,
    state.regionalSettings,
    state.graph,
    state.expenses,
  );
  const topRecommendation = getPlannerRecommendations(state)[0];
  return [
    `This plan models ${metrics.visitors.toLocaleString()} monthly visitors into about ${metrics.leads.toLocaleString()} leads and ${metrics.customers.toLocaleString()} customers.`,
    state.products.length > 0
      ? `Modeled monthly gross revenue is ${Math.round(pnl.grossRevenue).toLocaleString()} before refunds, fees, COGS, and traffic costs.`
      : "Revenue is estimated from the fallback revenue-per-lead assumption because no product is attached yet.",
    topRecommendation ? `Primary recommendation: ${topRecommendation.action}` : "",
  ].filter(Boolean).join(" ");
}
